import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { formatDistanceToNow } from "../auditUtils";
import JobsListPagination from "../JobsListPagination";
import { getListRangeLabel } from "../aiVisibilityListConstants";
import { formatLlmsTxtErrorForList, shouldShowLlmsTxtErrorMessage } from "./llmsTxtErrorUtils";
import {
	isLlmsTxtJobComplete,
	isLlmsTxtJobFailed,
} from "../../../services/llmsTxtApi";

function getStatusBadgeClass(job) {
	if (isLlmsTxtJobComplete(job?.status)) {
		return "writing-assistance__ready-badge";
	}
	if (isLlmsTxtJobFailed(job?.status)) {
		return "llms-txt__failed-badge";
	}
	return "writing-assistance__status-badge";
}

export default function LlmsTxtJobsCard({
	jobs,
	loading,
	total,
	page,
	pageSize,
	hasMore,
	onPageChange,
	onGenerate,
	generating,
}) {
	const navigate = useNavigate();
	const list = jobs ?? [];

	const openJob = (jobId) => {
		navigate(`/aivisibility/llms-txt/view?jobId=${encodeURIComponent(jobId)}`);
	};

	return (
		<Card className="shadow-sm llms-txt__jobs-card mt-4">
			<CardHeader className="bg-white border-bottom">
				<h5 className="mb-0">Your jobs</h5>
				<p className="text-muted small mb-0 mt-1">
					{loading
						? "Loading…"
						: `${total || list.length} job${(total || list.length) !== 1 ? "s" : ""} total · ${getListRangeLabel(
								page,
								pageSize,
								total || list.length
						  )}`}
				</p>
			</CardHeader>
			<CardBody>
				{loading ? (
					<div className="text-center py-4">
						<Spinner size="sm" />
					</div>
				) : list.length === 0 ? (
					<div className="text-center py-4">
						<p className="text-muted mb-3">No llms.txt jobs yet.</p>
						{onGenerate && (
							<Button color="primary" size="sm" onClick={onGenerate} disabled={generating}>
								Generate llms.txt
							</Button>
						)}
					</div>
				) : (
					<>
					<div className="d-flex flex-column gap-3">
						{list.map((job) => {
							const ready = isLlmsTxtJobComplete(job?.status);
							const failed = isLlmsTxtJobFailed(job?.status);

							return (
								<div key={job.id} className="llms-txt__job-row border rounded p-3">
									<div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
										<div className="d-flex flex-wrap align-items-center gap-2">
											{job.status_label && (
												<span className={getStatusBadgeClass(job)}>
													{ready && <i className="fas fa-check me-1" />}
													{failed && <i className="fas fa-times me-1" />}
													{job.status_label}
												</span>
											)}
											{job.created_at && (
												<span className="text-muted small">
													{formatDistanceToNow(job.created_at)}
												</span>
											)}
										</div>
										{ready && (
											<Button
												color="light"
												size="sm"
												className="llms-txt__view-btn border"
												onClick={() => openJob(job.id)}
											>
												<i className="far fa-file-alt me-1" />
												View output
											</Button>
										)}
										{failed && (
											<Button
												color="light"
												size="sm"
												className="llms-txt__view-btn border"
												onClick={() => openJob(job.id)}
											>
												<i className="fas fa-exclamation-circle me-1" />
												View details
											</Button>
										)}
									</div>
									{job.url && (
										<a
											href={job.url}
											target="_blank"
											rel="noopener noreferrer"
											className="llms-txt__job-url d-block text-break mb-0"
										>
											{job.url}
											<i className="fas fa-external-link-alt ms-1 small" />
										</a>
									)}
									{job.warning_message && (
										<p className="text-warning small mb-0 mt-2">{job.warning_message}</p>
									)}
									{job.error_message && shouldShowLlmsTxtErrorMessage(job.error_message) && (
										<p className="text-danger small mb-0 mt-2">
											{formatLlmsTxtErrorForList(job.error_message)}
										</p>
									)}
								</div>
							);
						})}
					</div>
					<JobsListPagination
						page={page}
						pageSize={pageSize}
						total={total}
						hasMore={hasMore}
						onPageChange={onPageChange}
					/>
					</>
				)}

			</CardBody>
		</Card>
	);
}
